/**
 * The Data surface's view of the CLI's one API client.
 *
 * There is one keyring login: these commands authenticate as WayAI and reach
 * base data through the WayAI backend's `/v1` proxy, which attests the org over
 * its service binding. No second
 * token type, no second `api_url`, no second config file.
 */

import { getAccessToken, requireAuth } from '../lib/auth.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import type { OrgTagLike } from '../lib/credential-utils.js';
import { readRepoConfigUnlessBlocked } from '../lib/repo-config.js';
import { UUID_RE } from '../lib/utils.js';
import { expected } from '../lib/expected.js';
import { getDataOrgOverride } from './org-context.js';

/**
 * Pages one `collectPages` call will follow. High enough that no real listing
 * reaches it, low enough that a server looping on fresh cursors stops.
 */
const MAX_PAGES = 1000;

export interface DataClient {
  /**
   * One request against a Data-surface path, returning its unwrapped `data`.
   *
   * The path is the Data backend's OWN path, verbatim — `/v1/…` for the tenant
   * surface and `/admin/…` for the platform-operator one. Only the central
   * prefix knows where WayAI mounts them; a command that spelled the mount would
   * have to be edited if the mount ever moved.
   *
   * `headers` carries the surface's own request headers, such as a file
   * delete's `If-Match`.
   */
  request<T = unknown>(
    method: string,
    path: string,
    body?: unknown,
    headers?: Record<string, string>,
  ): Promise<T>;
  /**
   * Like `request`, but returns the parsed body VERBATIM instead of unwrapping
   * `data`.
   *
   * For a route that answers with a BARE object rather than the `{ data, meta }`
   * envelope the rest of the surface uses. Through `request` such a response
   * unwraps to `undefined` — there is no `data` key — so every field on it is
   * silently lost and a caller that dereferences the result throws.
   *
   * `POST /v1/{base}/preview` is the one this CLI touches: it answers with the
   * cloned base itself, while its siblings `promote`, `promote/rollback` and
   * `promotions` all wrap in `{ data }`. Route the next bare-body caller through
   * here rather than widening `request`, whose unwrap every other call site
   * depends on.
   */
  requestRaw<T = unknown>(method: string, path: string, body?: unknown): Promise<T>;
  /**
   * Follow a cursor-paginated list to the end, accumulating every page.
   * `makePath(cursor)` builds the path for a page (`undefined` on the first),
   * so callers that must see a whole fleet aren't limited to the server's page.
   */
  collectPages<T>(makePath: (cursor: string | undefined) => string): Promise<T[]>;
  /**
   * Upload raw bytes to a `/v1` path — file content and record attachments.
   * `headers` carries the surface's own metadata headers.
   */
  upload<T = unknown>(
    v1Path: string,
    bytes: Uint8Array,
    contentType: string,
    headers?: Record<string, string>,
  ): Promise<T>;
  /** Download a `/v1` path's stored bytes rather than its JSON envelope. */
  download(v1Path: string): Promise<{ bytes: Uint8Array; contentType: string }>;
  /**
   * The absolute URL of a `/v1` path, for printing a link the user can fetch
   * themselves. It still requires their WayAI session — the Data surface is
   * reached only through the authenticated proxy.
   */
  url(v1Path: string): string;
  /**
   * The ORG's tag registry — the one deliberate exception to this client's
   * `/v1`-only contract above, reached here so the org it reads is the org this
   * client's writes attest to.
   *
   * It belongs on this client rather than beside its caller for that reason
   * alone: resolving the selector a second time produced a ladder that skipped
   * the `--org` UUID guard above, and validating a tag name against a different
   * org's vocabulary refuses names the server would accept. Where no selector
   * rides the invocation, the caller's default org is asked for — the same
   * fallback the backend's attestation makes for an unselected request.
   */
  listOrgTags(): Promise<OrgTagLike[]>;
  /**
   * Link / unlink an ORG credential onto a base — the second and third deliberate
   * exceptions to the `/v1`-only contract, and here for the same reason as `listOrgTags`:
   * these routes take an explicit organization, and it must be the org this client's
   * writes attest to. Resolving the selector a second time beside the caller is what
   * produced a ladder that skipped the `--org` UUID guard.
   */
  linkBaseCredential(
    credentialId: string,
    input: { base_id: string; name?: string },
  ): Promise<unknown>;
  unlinkBaseCredential(
    credentialId: string,
    baseCredentialId: string,
    input: { base_id: string },
  ): Promise<unknown>;
}

/**
 * Build the Data client for this invocation. The org selector resolves as
 * `--org` > the repo's org binding (`wayai-ws/wayai.yaml`) > absent, and absent means the backend uses
 * the caller's default org (refusing when they have none). `orgId` overrides the
 * recorded `--org` for the rare caller that must target a specific org
 * regardless of the flag.
 *
 * `refreshLogin` renews a browser login on a 401 and sends the request again, for
 * a command that runs past the login's lifetime (`wayai knowledge refresh-preview`).
 */
export async function createDataClient(
  orgId?: string,
  opts: { refreshLogin?: boolean } = {},
): Promise<DataClient> {
  const { config, accessToken } = await requireAuth();
  const api = new ApiClient({
    apiUrl: config.api_url,
    accessToken,
    ...(opts.refreshLogin ? { onUnauthorized: () => getAccessToken(config) } : {}),
  });
  const selected = orgId ?? getDataOrgOverride();
  if (selected !== undefined && !UUID_RE.test(selected)) {
    // The option is advertised as `--org <uuid>`; without this a typo travels to
    // the backend as an org selector and comes back as an opaque authorization
    // failure instead of naming the bad value.
    throw expected(`Invalid --org: ${JSON.stringify(selected)}. Expected an organization UUID.`);
  }
  // NOT `readRepoConfig`: its null conflates "no binding declared" with "one is
  // declared and cannot be resolved", and absent here means the BACKEND picks
  // the caller's default org. A repo whose two config files disagree would
  // therefore push a base into an organization neither file named, and exit 0.
  const org = selected ?? readRepoConfigUnlessBlocked()?.organization_id;

  /**
   * The org a `/api/setup/*` route must be told about explicitly.
   *
   * Those routes take an org rather than inferring one, so an unselected invocation has to
   * NAME the org its writes would attest to rather than omit it —
   * `resolveOrgFromRequest` falls back to the caller's default org, and `/api/auth/me` is
   * where that value is readable. One copy for all three callers: resolving it per caller
   * is what produced a ladder that skipped the `--org` UUID guard above, and reading one
   * org's vocabulary while writing to another's refuses names the server would accept.
   */
  async function requireOrgForSetupRoute(what: string): Promise<string> {
    const orgId = org ?? (await api.me()).org_id;
    if (!orgId) {
      throw expected(
        `No organization to ${what} against. Pass --org <uuid>, or run this from a repo scoped by \`wayai init\`.`,
      );
    }
    return orgId;
  }

  return {
    async request<T>(
      method: string,
      path: string,
      body?: unknown,
      headers?: Record<string, string>,
    ): Promise<T> {
      const envelope = await api.dataRequest<T>(method, path, body, org, headers);
      return envelope.data as T;
    },
    async requestRaw<T>(method: string, path: string, body?: unknown): Promise<T> {
      // `dataRequest` returns the parsed body as the envelope; for a bare-body
      // route that object IS the response, so it is returned rather than read
      // through `.data`.
      const body_ = await api.dataRequest<unknown>(method, path, body, org);
      return body_ as T;
    },
    async collectPages<T>(makePath: (cursor: string | undefined) => string): Promise<T[]> {
      const all: T[] = [];
      const seen = new Set<string>();
      let cursor: string | undefined;
      for (let page = 0; page < MAX_PAGES; page++) {
        const { data, meta } = await api.dataRequest<T[]>('GET', makePath(cursor), undefined, org);
        // `push(...page)` spreads into call arguments and throws RangeError once
        // a page is large enough — and whole-fleet listings are exactly what this
        // exists for.
        for (const row of data ?? []) all.push(row);
        cursor = meta?.has_more ? meta.cursor : undefined;
        if (cursor === undefined) break;
        // Two independent stops, because they fail differently: a REPEATED cursor
        // is a server bug that would spin forever on one page, while ever-fresh
        // cursors with `has_more` would spin while `all` grows until the process
        // dies. Neither should hang a command the user then has to kill.
        if (seen.has(cursor)) break;
        seen.add(cursor);
      }
      return all;
    },
    async upload<T>(
      v1Path: string,
      bytes: Uint8Array,
      contentType: string,
      headers?: Record<string, string>,
    ): Promise<T> {
      const envelope = await api.dataUpload<T>(v1Path, bytes, contentType, org, headers);
      return envelope.data as T;
    },
    download(v1Path: string) {
      return api.dataDownload(v1Path, org);
    },
    url(v1Path: string): string {
      // Threads the same org selector every other call carries: without it the
      // printed link resolves against the caller's DEFAULT org, which a platform
      // admin does not have and a multi-org user may not want — so the link
      // fails where the CLI's own request to the same key succeeds.
      return api.dataUrl(v1Path, org);
    },
    async listOrgTags(): Promise<OrgTagLike[]> {
      return (await api.listOrgTags(await requireOrgForSetupRoute('resolve tags'))).data ?? [];
    },
    async linkBaseCredential(credentialId, input) {
      const orgId = await requireOrgForSetupRoute('link a credential');
      return api.linkOrgCredentialToBase(credentialId, { organization_id: orgId, ...input });
    },
    async unlinkBaseCredential(credentialId, baseCredentialId, input) {
      const orgId = await requireOrgForSetupRoute('unlink a credential');
      return api.unlinkOrgCredentialFromBase(credentialId, baseCredentialId, {
        organization_id: orgId,
        ...input,
      });
    },
  };
}

/**
 * The Data error envelope's `error.code` / `error.details`, for the handful of
 * commands whose recovery depends on WHICH failure this was rather than on the
 * status alone — a retryable shed versus an ambiguous timeout, an expired import
 * session versus a deliberately closed one, a revoke the server wants confirmed.
 *
 * Read off `ApiError.body`, which is the SECRET-MASKED body. That is deliberate:
 * these read control fields (`code`, `status`, `requires_force`), never
 * credential material, so there is no reason for an unmasked copy to exist.
 */
function dataErrorEnvelope(err: unknown): { code?: unknown; details?: unknown } | null {
  if (!(err instanceof ApiError)) return null;
  try {
    const parsed = JSON.parse(err.body) as { error?: { code?: unknown; details?: unknown } };
    return parsed?.error ?? null;
  } catch {
    return null;
  }
}

/** The envelope's `error.code`, or null when absent/unparseable. */
export function dataErrorCode(err: unknown): string | null {
  const code = dataErrorEnvelope(err)?.code;
  return typeof code === 'string' && code ? code : null;
}

/** The envelope's `error.details` object, or undefined when absent/unparseable. */
export function dataErrorDetails(err: unknown): Record<string, unknown> | undefined {
  const details = dataErrorEnvelope(err)?.details;
  return details && typeof details === 'object' ? (details as Record<string, unknown>) : undefined;
}
